/**
 * ARRIVAL RECORDS · one authority for "which screen is this".
 *
 * A decrement between two ARRIVALS answers "did they move on"; a decrement to a
 * COMPLETION answers "did they finish the action". Mixing the two in one column
 * reads as a cliff that is really a missing event, which is exactly what the
 * pre-restructure instrumentation did — so arrivals are recorded here, once per
 * screen per visit, and the existing completion events are left untouched.
 *
 * The allowed keys live in the database (`funnel_states`), not in a second list:
 * this map only says which screen corresponds to which key.
 */
import { recordFunnelEvent, type FunnelState } from "@/lib/consumer-session";

/** Route segment under /create → arrival state. */
export const ARRIVAL_BY_SEGMENT: Record<string, FunnelState> = {
  // "style" is deliberately absent: S1 records `landed` on mount already, and a
  // second key for the same moment would double-count the first row of the table.
  selfie: "arrived_selfie",
  "same-face": "arrived_same_face",
  verify: "arrived_consent",
  share: "arrived_share",
  name: "arrived_name",
  phone: "arrived_phone",
  dob: "arrived_dob",
  address: "arrived_address",
  fee: "arrived_fee",
  load: "arrived_load",
  pay: "arrived_pay",
  authorise: "arrived_authorise",
  confirmed: "arrived_confirmed",
  // `generating` and `revealed` are already arrival-shaped and keep their keys,
  // recorded by their own screens where the variant count is known.
};

export function arrivalForPath(pathname: string): FunnelState | null {
  const segment = pathname.replace(/\/+$/, "").split("/").pop() ?? "";
  return ARRIVAL_BY_SEGMENT[segment] ?? null;
}

/**
 * Once per screen per page-load session. Re-entering a screen after going back
 * is NOT a second arrival — a repeat would inflate the step above it.
 */
const seen = new Set<string>();

export function recordArrival(slug: string, pathname: string, programId?: string | null): void {
  const state = arrivalForPath(pathname);
  if (!state) return;
  const key = `${slug}:${state}`;
  if (seen.has(key)) return;
  seen.add(key);
  recordFunnelEvent(slug, state, programId ?? null);
}

/** Test seam only. */
export function __resetArrivals() {
  seen.clear();
}
